import { useEffect, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';

const encode = (str: string): string => str;

/**
 * Hook personalizado para persistir estado e componentes
 * @param defaultKey Chave para armazenamento
 * @param defaultValue Valor padrão
 * @param options Configurações adicionais
 */
export function usePersistedState<T>(
  defaultKey: string,
  defaultValue: T,
  options?: {
    isComponent?: boolean; // Flag para indicar que é um componente
    componentMap?: Record<string, React.ComponentType<any>>; // Mapeamento de componentes
  }
) {
  const key = encode(defaultKey);
  const [state, setState] = useState<T>(() => {
    if (typeof window === 'undefined') return defaultValue;

    const storedValue = sessionStorage.getItem(key);
    if (!storedValue) return defaultValue;

    try {
      if (options?.isComponent && options.componentMap) {
        const componentNames = JSON.parse(storedValue) as string[];
        return componentNames.map((id) =>
          Object.values(options.componentMap || {}).find(
            (item: any) => item.id === id
          )
        ) as T;
      }
      return JSON.parse(storedValue) as T;
    } catch {
      return defaultValue;
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      if (options?.isComponent && Array.isArray(state)) {
        const componentNames = (state as React.ComponentType<any>[])
          .map((comp) => (comp as any).id)
          .filter(Boolean);
        sessionStorage.setItem(key, JSON.stringify(componentNames));
      } else {
        sessionStorage.setItem(key, JSON.stringify(state));
      }
    } catch (error) {
      console.error('Failed to persist state:', error);
    }
  }, [key, state, options?.isComponent]);

  return [state, setState] as const;
}

type UsePersistedFormOptions<T extends Record<string, any>> = {
  defaultValues: T;
  storageKey: string;
  formOptions?: Parameters<typeof useForm<T>>[0];
};

/**
 * Hook useForm personalizado com persistência automática
 * @param options Configurações do formulário
 */
export function usePersistedForm<T extends Record<string, any>>({
  defaultValues,
  storageKey,
  formOptions
}: UsePersistedFormOptions<T | any>): UseFormReturn<T> {
  const key = encodeURIComponent(storageKey);

  // Carrega os valores salvos de forma síncrona
  const loadSavedValues = () => {
    if (typeof window === 'undefined') return defaultValues;
    try {
      const storedData = sessionStorage.getItem(key);
      return storedData ? JSON.parse(storedData) : defaultValues;
    } catch {
      return defaultValues;
    }
  };

  const methods = useForm<T>({
    ...formOptions,
    defaultValues: loadSavedValues()
  });

  // Persiste os dados do formulário
  useEffect(() => {
    const subscription = methods.watch((data) => {
      try {
        sessionStorage.setItem(key, JSON.stringify(data));
      } catch (error) {
        console.error('Error saving form data:', error);
      }
    });

    return () => subscription.unsubscribe();
  }, [key, methods.watch]);

  return methods;
}
